import * as React from 'react'
import {
  useComboboxInput,
  useHTMLInputCursorState,
} from '@platejs/combobox/react'
import type { PointRef } from 'platejs'
import { useEditorRef, useElement, useSelected } from 'platejs/react'
import { useRichTextFieldContext } from '../RichTextField'

type InlineComboboxInputLifecycle = {
  inputRef: React.RefObject<HTMLInputElement>
  composing: React.MutableRefObject<boolean>
  focused: boolean
  setFocused: React.Dispatch<React.SetStateAction<boolean>>
  onKeyDown: React.KeyboardEventHandler<HTMLInputElement>
  cancel: () => void
  resume: () => void
  endComposition: (query: string) => void
  select: (callback: () => void) => void
}

export function useInlineComboboxInput({
  trigger,
  value,
  locked,
}: {
  trigger: string
  value: string
  locked: boolean
}): InlineComboboxInputLifecycle {
  const editor = useEditorRef()
  const element = useElement()
  const { editableRef } = useRichTextFieldContext()
  const selected = useSelected()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [focused, setFocused] = React.useState(false)
  const composing = React.useRef(false)
  const pendingCancellation = React.useRef(false)
  const query = React.useRef(value)
  query.current = value
  const point = React.useRef<PointRef | null>(null)
  const shouldAutoFocus = React.useRef(!locked && selected)
  const cursorState = useHTMLInputCursorState(inputRef)
  const hasInput = (): boolean => {
    const location = editor.api.findPath(element)
    return !!location && editor.api.node(location)?.[0] === element
  }

  React.useLayoutEffect(() => {
    const location = editor.api.findPath(element)
    const before = location && editor.api.before(location)
    if (before)
      point.current = editor.api.pointRef(before, { affinity: 'backward' })
    return (): void => {
      point.current?.unref()
    }
  }, [editor, element])

  React.useEffect(() => {
    if (locked) pendingCancellation.current = false
  }, [locked])
  React.useEffect(() => {
    if (!selected && composing.current) pendingCancellation.current = true
  }, [selected])

  const hook = useComboboxInput({
    ref: inputRef,
    autoFocus: shouldAutoFocus.current,
    cursorState,
    cancelInputOnBackspace: !locked && value === '',
    cancelInputOnDeselect: !locked && !composing.current,
    cancelInputOnEscape: !locked,
    cancelInputOnArrowLeftRight: !locked,
    onCancelInput: (cause) => {
      const insertion = point.current?.current
      if (!insertion) return
      setFocused(false)
      const text = trigger + query.current
      if (cause !== 'backspace') editor.tf.insertText(text, { at: insertion })
      if (cause === 'blur' || cause === 'deselect') return
      editor.tf.select({
        path: insertion.path,
        offset:
          insertion.offset +
          (cause === 'arrowLeft' || cause === 'backspace' ? 0 : text.length),
      })
      editableRef.current?.focus({ preventScroll: true })
    },
  })

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (
      locked ||
      composing.current ||
      event.nativeEvent.isComposing ||
      !hasInput()
    )
      return
    const { selectionStart, selectionEnd } = event.currentTarget
    const boundary =
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight' ||
      event.key === 'Backspace'
    if (boundary && selectionStart !== selectionEnd) return
    const plain =
      !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey
    if (
      plain &&
      (event.key === 'Escape' ||
        (event.key === 'ArrowLeft' && cursorState.atStart) ||
        (event.key === 'ArrowRight' && cursorState.atEnd) ||
        (event.key === 'Backspace' && cursorState.atStart && value === ''))
    ) {
      event.preventDefault()
      event.stopPropagation()
    }
    hook.props.onKeyDown(event)
  }
  return {
    inputRef,
    composing,
    focused,
    setFocused,
    onKeyDown,
    cancel: (): void => {
      if (locked || !hasInput()) return
      if (composing.current) {
        pendingCancellation.current = true
        return
      }
      hook.cancelInput('blur')
    },
    resume: (): void => {
      pendingCancellation.current = false
      if (!locked) setFocused(true)
    },
    endComposition: (text: string): void => {
      composing.current = false
      query.current = text
      const pending = pendingCancellation.current
      pendingCancellation.current = false
      const input = inputRef.current
      if (
        pending &&
        !locked &&
        hasInput() &&
        (!selected || input?.ownerDocument.activeElement !== input)
      )
        hook.cancelInput('blur')
    },
    select: (callback: () => void): void => {
      if (locked || composing.current || !hasInput()) return
      hook.removeInput()
      setFocused(false)
      // Focus must finish before the consumer can open another control.
      editableRef.current?.focus({ preventScroll: true })
      callback()
    },
  }
}
